import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FiSend, FiMessageSquare, FiPlus, FiDownload, FiEye, FiTrash2, FiCheckSquare, FiSquare, FiRotateCcw, FiEyeOff } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { chatbotAPI, documentsAPI } from '../lib/api';

// Simple notification helper (replace with toast library if preferred)
const notify = {
  success: (msg) => alert(msg),
  error: (msg) => alert(`Error: ${msg}`)
};

const Chatbot = () => {
  const [message, setMessage] = useState('');
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedConversations, setSelectedConversations] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const messagesEndRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const queryClient = useQueryClient();

  // Function to parse message content and make document names clickable
  const parseMessageContent = (content) => {
    if (!content) return content;

    // Find all documents that could be referenced
    const availableDocs = documents || [];

    // Create a more flexible matching function
    const findMatchingDoc = (text) => {
      // Clean the text of markdown and special characters
      const cleanText = text.replace(/\*\*/g, '').replace(/\*/g, '').trim();

      // First try exact match
      let match = availableDocs.find(doc =>
        doc.name.toLowerCase() === cleanText.toLowerCase()
      );
      if (match) return match;

      // Try partial match (document name contains the text or vice versa)
      match = availableDocs.find(doc =>
        doc.name.toLowerCase().includes(cleanText.toLowerCase()) ||
        cleanText.toLowerCase().includes(doc.name.toLowerCase())
      );
      if (match) return match;

      // Try fuzzy match by removing file extensions and comparing
      const textWithoutExt = cleanText.replace(/\.[^/.]+$/, '');
      match = availableDocs.find(doc => {
        const docNameWithoutExt = doc.name.replace(/\.[^/.]+$/, '');
        return docNameWithoutExt.toLowerCase() === textWithoutExt.toLowerCase() ||
               docNameWithoutExt.toLowerCase().includes(textWithoutExt.toLowerCase());
      });

      return match;
    };

    // Split content by lines and process each line
    const lines = content.split('\n');
    const result = [];

    lines.forEach((line, lineIndex) => {
      // First, detect and process URLs (including SharePoint URLs)
      const urlPattern = /(https?:\/\/[^\s<]+)/g;
      const segments = [];
      let lastUrlIndex = 0;

      // Split line by URLs first
      let urlMatch;
      while ((urlMatch = urlPattern.exec(line)) !== null) {
        // Add text before URL
        if (urlMatch.index > lastUrlIndex) {
          segments.push({ type: 'text', content: line.substring(lastUrlIndex, urlMatch.index) });
        }
        // Add URL
        segments.push({ type: 'url', content: urlMatch[0] });
        lastUrlIndex = urlMatch.index + urlMatch[0].length;
      }
      // Add remaining text
      if (lastUrlIndex < line.length) {
        segments.push({ type: 'text', content: line.substring(lastUrlIndex) });
      }
      // If no URLs found, treat entire line as text
      if (segments.length === 0) {
        segments.push({ type: 'text', content: line });
      }

      // Now process each segment
      const lineParts = [];
      segments.forEach((segment, segIndex) => {
        if (segment.type === 'url') {
          // Render URL as clickable link
          const isSharePoint = segment.content.includes('sharepoint.com') || segment.content.includes('my.sharepoint.com');
          lineParts.push(
            <a
              key={`url-${lineIndex}-${segIndex}`}
              href={segment.content}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg border transition-all hover:underline ${
                isSharePoint
                  ? 'bg-blue-50 text-blue-700 border-blue-200 hover:border-blue-400'
                  : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-400'
              }`}
              title="Open link in new tab"
            >
              {isSharePoint && <span className="text-xs font-semibold">SharePoint</span>}
              <FiEye className="w-3 h-3" />
              {isSharePoint ? 'Open Document' : 'Link'}
            </a>
          );
        } else {
          // Process text segment for document name matches
          const textContent = segment.content;
          const docPattern = /(\*\*)?([A-Za-z0-9_\-\s,().&+#@\[\]]+\.[a-zA-Z]{2,5})(\*\*)?/g;
          let lastIndex = 0;
          let match;

          while ((match = docPattern.exec(textContent)) !== null) {
            const fullMatch = match[0];
            const fileName = match[2];
            const matchingDoc = findMatchingDoc(fileName);

            // Add text before the match
            if (match.index > lastIndex) {
              lineParts.push(textContent.substring(lastIndex, match.index));
            }

            if (matchingDoc) {
              lineParts.push(
                <span key={`doc-${lineIndex}-${segIndex}-${match.index}`} className="inline-flex items-center gap-2 bg-blue-50 px-2 py-1 rounded-lg border border-blue-200 hover:border-blue-400 transition-all group/doc my-1">
                  <FiEye className="w-4 h-4 text-blue-600" />
                  <Link
                    to={`/documents/${matchingDoc.id}`}
                    className="text-primary-600 hover:text-primary-800 font-semibold"
                    title={`View ${matchingDoc.name}`}
                  >
                    {fileName}
                  </Link>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleDownload(matchingDoc);
                    }}
                    className="text-gray-500 hover:text-primary-600 p-1 rounded hover:bg-blue-100 transition-colors"
                    title={`Download ${matchingDoc.name}`}
                  >
                    <FiDownload className="w-4 h-4" />
                  </button>
                </span>
              );
            } else {
              lineParts.push(fullMatch);
            }

            lastIndex = match.index + fullMatch.length;
          }

          // Add remaining text from this segment
          if (lastIndex < textContent.length) {
            lineParts.push(textContent.substring(lastIndex));
          }
        }
      });

      result.push(
        <div key={lineIndex}>
          {lineParts.length > 0 ? lineParts : line}
        </div>
      );
    });

    return result;
  };

  const handleDownload = async (doc) => {
    try {
      const response = await documentsAPI.download(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const { data: conversationsResponse } = useQuery({
    queryKey: ['conversations', showDeleted],
    queryFn: () => chatbotAPI.getConversations(showDeleted),
  });

  const { data: messagesResponse } = useQuery({
    queryKey: ['messages', currentConversationId],
    queryFn: () => chatbotAPI.getMessages(currentConversationId),
    enabled: !!currentConversationId,
  });

  const { data: documentsResponse } = useQuery({
    queryKey: ['documents', 'all'],
    queryFn: () => documentsAPI.getAll({ limit: 100 }),
  });

  // Extract actual data from axios responses
  const conversations = conversationsResponse?.data;
  const messages = messagesResponse?.data;
  const documents = documentsResponse?.data?.documents || [];

  // Start polling for background processing result
  const startPolling = (conversationId) => {
    setIsPolling(true);
    const startTime = Date.now();

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await chatbotAPI.poll(conversationId);
        if (response.data.status === 'complete') {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setIsPolling(false);
          await queryClient.invalidateQueries(['messages', conversationId]);
          await queryClient.invalidateQueries(['conversations']);
        } else if (Date.now() - startTime > 120000) {
          // Safety timeout: stop polling after 2 minutes
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setIsPolling(false);
        }
      } catch (err) {
        console.error('Polling error:', err);
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        setIsPolling(false);
      }
    }, 2000);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const sendMutation = useMutation({
    mutationFn: (data) => chatbotAPI.query(data),
    onSuccess: async (response) => {
      // Extract the conversation_id from the backend response
      const newConversationId = response.data.conversation_id;

      // Update the current conversation ID to the new one
      setCurrentConversationId(newConversationId);

      if (response.data.status === 'processing') {
        // Backend couldn't finish in time — poll for the result
        await queryClient.invalidateQueries(['messages', newConversationId]);
        await queryClient.invalidateQueries(['conversations']);
        setMessage('');
        startPolling(newConversationId);
      } else {
        // Result came back synchronously
        await queryClient.invalidateQueries(['messages', newConversationId]);
        await queryClient.invalidateQueries(['conversations']);
        setMessage('');
      }
    },
  });

  const softDeleteMutation = useMutation({
    mutationFn: (conversationIds) => chatbotAPI.softDelete(conversationIds),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['conversations']);
      setSelectedConversations([]);
      setSelectionMode(false);
      notify.success(response.data.message || 'Conversations deleted successfully');
      // If current conversation was deleted, clear it
      if (selectedConversations.includes(currentConversationId)) {
        setCurrentConversationId(null);
      }
    },
    onError: (error) => {
      notify.error(error.response?.data?.error || 'Failed to delete conversations');
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (conversationIds) => chatbotAPI.restore(conversationIds),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['conversations']);
      setSelectedConversations([]);
      setSelectionMode(false);
      notify.success(response.data.message || 'Conversations restored successfully');
    },
    onError: (error) => {
      notify.error(error.response?.data?.error || 'Failed to restore conversations');
    },
  });

  const handleSend = (e) => {
    e.preventDefault();
    if (message.trim()) {
      // If no conversation selected, backend will create one
      const payload = {
        message: message.trim(),
      };

      // Only include context if documents are explicitly selected
      if (selectedDocumentIds.length > 0) {
        payload.context = {
          document_ids: selectedDocumentIds,
        };
      }

      if (currentConversationId) payload.conversation_id = currentConversationId;

      sendMutation.mutate(payload, {
        onSuccess: (response) => {
          // Set the conversation ID from response if it was just created
          if (response?.data?.conversation_id && !currentConversationId) {
            setCurrentConversationId(response.data.conversation_id);
          }
        },
      });
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleToggleSelection = (convId) => {
    setSelectedConversations((prev) =>
      prev.includes(convId) ? prev.filter((id) => id !== convId) : [...prev, convId]
    );
  };

  const handleSelectAll = () => {
    const allIds = conversations?.conversations?.map((c) => c.id) || [];
    setSelectedConversations(allIds);
  };

  const handleClearSelection = () => {
    setSelectedConversations([]);
  };

  const handleClearSelected = () => {
    if (selectedConversations.length === 0) {
      notify.error('No conversations selected');
      return;
    }
    softDeleteMutation.mutate(selectedConversations);
  };

  const handleClearAll = () => {
    const allIds = conversations?.conversations
      ?.filter((c) => !c.is_deleted)
      .map((c) => c.id) || [];

    if (allIds.length === 0) {
      notify.error('No conversations to delete');
      return;
    }

    if (window.confirm(`Are you sure you want to delete all ${allIds.length} conversation(s)?`)) {
      softDeleteMutation.mutate(allIds);
    }
  };

  const handleRestoreSelected = () => {
    if (selectedConversations.length === 0) {
      notify.error('No conversations selected');
      return;
    }
    restoreMutation.mutate(selectedConversations);
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">AI Assistant</h1>
        <p className="text-gray-600 mt-2">Get help with your documents and data</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Conversations Sidebar */}
        <div className="lg:col-span-1">
          <div className="card p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Conversations</h2>
              <button
                onClick={() => setShowDeleted(!showDeleted)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title={showDeleted ? 'Hide deleted' : 'Show deleted'}
              >
                {showDeleted ? (
                  <FiEye className="w-4 h-4 text-gray-600" />
                ) : (
                  <FiEyeOff className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>

            {/* New Conversation Button */}
            <button
              onClick={() => {
                setCurrentConversationId(null);
                setSelectionMode(false);
                setSelectedConversations([]);
              }}
              className="btn-primary w-full mb-4 flex items-center justify-center space-x-2"
            >
              <FiPlus className="w-4 h-4" />
              <span>New Conversation</span>
            </button>

            {/* Selection Mode Controls */}
            {!selectionMode ? (
              <button
                onClick={() => setSelectionMode(true)}
                className="w-full mb-4 py-2 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2 text-sm text-gray-700"
              >
                <FiCheckSquare className="w-4 h-4" />
                <span>Select Conversations</span>
              </button>
            ) : (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-2">
                <div className="flex items-center justify-between text-xs font-medium text-blue-900 mb-2">
                  <span>Selection Mode</span>
                  <span>{selectedConversations.length} selected</span>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={handleSelectAll}
                    className="px-2 py-1.5 text-xs bg-white border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                  >
                    All
                  </button>
                  <button
                    onClick={handleClearSelection}
                    className="px-2 py-1.5 text-xs bg-white border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                  >
                    None
                  </button>
                  <button
                    onClick={() => {
                      setSelectionMode(false);
                      setSelectedConversations([]);
                    }}
                    className="px-2 py-1.5 text-xs bg-white border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>

                {!showDeleted ? (
                  <div className="space-y-2 pt-2 border-t border-blue-200">
                    <button
                      onClick={handleClearSelected}
                      disabled={selectedConversations.length === 0 || softDeleteMutation.isPending}
                      className="w-full py-2 px-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 text-sm"
                    >
                      <FiTrash2 className="w-4 h-4" />
                      <span>Delete ({selectedConversations.length})</span>
                    </button>
                    <button
                      onClick={handleClearAll}
                      disabled={softDeleteMutation.isPending}
                      className="w-full py-2 px-3 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                    >
                      Delete All
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleRestoreSelected}
                    disabled={selectedConversations.length === 0 || restoreMutation.isPending}
                    className="w-full mt-2 py-2 px-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 text-sm"
                  >
                    <FiRotateCcw className="w-4 h-4" />
                    <span>Restore ({selectedConversations.length})</span>
                  </button>
                )}
              </div>
            )}

            {/* Conversations List */}
            <div className="space-y-1 max-h-[500px] overflow-y-auto">
              {conversations?.conversations?.map((conv) => (
                <div
                  key={conv.id}
                  onClick={() => {
                    if (selectionMode) {
                      handleToggleSelection(conv.id);
                    } else {
                      setCurrentConversationId(conv.id);
                    }
                  }}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                    currentConversationId === conv.id && !selectionMode
                      ? 'bg-primary-50 border-l-4 border-primary-600'
                      : 'hover:bg-gray-50 border-l-4 border-transparent'
                  } ${conv.is_deleted ? 'opacity-50' : ''} ${
                    selectionMode && selectedConversations.includes(conv.id)
                      ? 'bg-blue-50 border-l-4 border-blue-600'
                      : ''
                  } flex items-start space-x-3`}
                >
                  {selectionMode && (
                    <div className="flex-shrink-0 mt-0.5">
                      {selectedConversations.includes(conv.id) ? (
                        <FiCheckSquare className="w-5 h-5 text-primary-600" />
                      ) : (
                        <FiSquare className="w-5 h-5 text-gray-300" />
                      )}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-sm truncate ${
                      currentConversationId === conv.id && !selectionMode
                        ? 'text-primary-900'
                        : 'text-gray-900'
                    }`}>
                      {conv.title || 'New Conversation'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(conv.updated_at).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                      {conv.is_deleted && <span className="ml-2 text-red-500">(Deleted)</span>}
                    </p>
                  </div>
                </div>
              ))}
              {conversations?.conversations?.length === 0 && (
                <div className="text-center py-8">
                  <FiMessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">
                    {showDeleted ? 'No deleted conversations' : 'No conversations yet'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div className="lg:col-span-3">
          <div className="card h-[600px] flex flex-col">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto mb-4 space-y-4">
              {messages?.messages?.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-4 rounded-lg shadow-sm ${
                      msg.role === 'user'
                        ? 'bg-primary-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-900'
                    }`}
                  >
                    <div className={msg.role === 'user' ? 'whitespace-pre-wrap' : 'space-y-2'}>
                      {msg.role === 'assistant' ? parseMessageContent(msg.content) : msg.content}
                    </div>
                    <p className="text-xs mt-3 opacity-70 border-t border-current pt-2 mt-2">
                      {new Date(msg.created_at).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}
              {/* AI Processing Indicator */}
              {(sendMutation.isPending || isPolling) && (
                <div className="flex justify-start">
                  <div className="max-w-[80%] p-4 rounded-lg shadow-sm bg-white border border-gray-200 text-gray-900">
                    <div className="flex items-center space-x-3">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-primary-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-primary-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-primary-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                      <span className="text-sm text-gray-600 font-medium">
                        {isPolling ? 'AI is analyzing your documents — this may take a moment...' : 'AI is processing your request...'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {!currentConversationId && !messages?.messages?.length && (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <FiMessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">
                      Start a conversation by typing a message below
                    </p>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSend} className="flex items-center space-x-3">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Ask me anything about your documents..."
                className="input flex-1"
                disabled={sendMutation.isPending || isPolling}
              />
              <button
                type="submit"
                disabled={!message.trim() || sendMutation.isPending || isPolling}
                className="btn-primary flex items-center space-x-2"
              >
                <FiSend />
                <span>{sendMutation.isPending ? 'Sending...' : isPolling ? 'Processing...' : 'Send'}</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
